// src/utils/localSpellCheck.js
// -----------------------------
// Client-side (in-browser) spell checker for the Manuscript Editor Tool tab.
//
// Runs entirely on the reader's machine using Typo.js (a pure-JS Hunspell — the
// same engine Word/LibreOffice use). Nothing is sent to the server, so:
//   • the EC2 backend does ZERO work,
//   • the manuscript text never leaves the browser (best privacy), and
//   • it behaves identically on desktop, Android, and iOS.
//
// The dictionary (~550 KB) is fetched once from /public and cached for the
// session. checkText() returns the SAME match shape the backend LanguageTool
// proxy produced, so SpellCheckModal's DOM-range apply/highlight code is
// unchanged — only the source of matches differs. Grammar is out of scope here
// (Typo.js checks words, not sentences); flip LANGUAGETOOL_URL back on when the
// infra can host LanguageTool and grammar returns automatically.

import Typo from 'typo-js';

const DICT_BASE = `${process.env.PUBLIC_URL || ''}/dictionaries/en_US`;

// Match runs of word characters (letters, apostrophes) and remember where each
// starts so we can map a misspelling back to an offset in the model text. We
// treat hyphens as boundaries so "well-known" is checked as "well" + "known".
const WORD_RE = /[A-Za-z][A-Za-z'’]*/g;

// One Typo instance per session, loaded lazily. The in-flight promise is cached
// too so concurrent checks don't kick off two downloads.
let dictionaryPromise = null;

const loadDictionary = () => {
  if (dictionaryPromise) return dictionaryPromise;
  dictionaryPromise = (async () => {
    const [aff, dic] = await Promise.all([
      fetch(`${DICT_BASE}/en_US.aff`).then((r) => {
        if (!r.ok) throw new Error(`dictionary aff ${r.status}`);
        return r.text();
      }),
      fetch(`${DICT_BASE}/en_US.dic`).then((r) => {
        if (!r.ok) throw new Error(`dictionary dic ${r.status}`);
        return r.text();
      }),
    ]);
    return new Typo('en_US', aff, dic, { platform: 'any' });
  })().catch((err) => {
    // Reset so a later attempt can retry the download instead of caching failure.
    dictionaryPromise = null;
    throw new Error('Could not load the spelling dictionary. Please try again.');
  });
  return dictionaryPromise;
};

// A token we should not flag. Skips single letters, all-caps acronyms (URL, NASA)
// and numbers-adjacent noise, which keeps false positives down on prose.
const shouldSkip = (word) => {
  if (word.length < 2) return true;
  if (word === word.toUpperCase()) return true; // acronym / initialism
  return false;
};

// Check a plain-text string and return an array of spelling matches in the shape
// SpellCheckModal expects: { offset, length, message, shortMessage,
// replacements[], ruleType, ruleId, context }. Offsets index into `text`.
export const checkText = async (text) => {
  const dictionary = await loadDictionary();
  if (!text) return [];

  // suggest() is the expensive call, so compute it once per UNIQUE misspelled
  // word and reuse across every occurrence.
  const suggestionCache = new Map();
  const matches = [];

  let m;
  WORD_RE.lastIndex = 0;
  while ((m = WORD_RE.exec(text)) !== null) {
    const word = m[0];
    if (shouldSkip(word)) continue;

    // Typo.js treats the curly apostrophe as a different char; normalise so
    // "don’t" is recognised the same as "don't".
    const probe = word.replace(/’/g, "'");
    if (dictionary.check(probe)) continue;

    let replacements = suggestionCache.get(probe);
    if (replacements === undefined) {
      replacements = (dictionary.suggest(probe) || []).slice(0, 8);
      suggestionCache.set(probe, replacements);
    }

    matches.push({
      offset: m.index,
      length: word.length,
      message: replacements.length
        ? `“${word}” may be misspelled.`
        : `“${word}” is not in the dictionary.`,
      shortMessage: 'Spelling',
      replacements,
      ruleType: 'spelling',
      ruleId: 'TYPO',
      context: '',
    });
  }

  return matches;
};

// Warm the dictionary ahead of the first check (optional; called when the modal
// mounts so the first run feels instant). Safe to call repeatedly.
export const preloadDictionary = () => {
  loadDictionary().catch(() => { /* surfaced later by checkText */ });
};
